import type { DependencyList } from 'react'
import { useEffect, useState } from 'react'

export type FetchedData<D> = [D | undefined, () => Promise<D | undefined>, boolean]
export type FetchedOrInitialData<D> = [D, () => Promise<D | undefined>, boolean]

export function useFetchedData<D>(callback: () => Promise<D>, compare?: DependencyList): FetchedData<D>
export function useFetchedData<D>(
  callback: () => Promise<D>,
  compare: DependencyList,
  initial: D,
): FetchedOrInitialData<D>
export function useFetchedData<D>(
  callback: () => Promise<D>,
  compare: DependencyList = [],
  initial?: D,
): FetchedData<D> {
  const [data, setData] = useState(initial)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(
    () => {
      let subscribed = true
      setIsLoading(true)
      callback()
        .then((res) => {
          if (subscribed) {
            setData(res)
            setIsLoading(false)
          }
        })
        .catch(() => subscribed && setIsLoading(false))
      return () => {
        subscribed = false
      }
    },
    // Compare can not be verified statically by eslint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    compare,
  )

  const refetch = async () => {
    setIsLoading(true)
    try {
      const res = await callback()
      setData(res)
      return res
    } catch {
      // do nothing
    } finally {
      setIsLoading(false)
    }
  }

  return [data, refetch, isLoading]
}
